import Button from '@mui/material/Button';
import ytShortsImage from 'assets/img/ytShortsTemp.png';
import Typography from '@mui/material/Typography';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useEffect, useRef, useState } from 'react';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Box from '@mui/material/Box';

export interface Short {
  text?: string;
  url?: string;
}

const embedUrl = (id: string) => `https://www.youtube.com/embed/${id}?controls=0&modestbranding&rel=0`;

export const DEFAULT_SHORT_URLS: string[] = ['qSmcqGbN4Zo', 'BgsYfLuAF8E', 'CvNNfWtFSrQ', 'R5D1ZcIhe58', 'JW1UCbe0FPc'].map(embedUrl);

export type YouTubeShortsProps = {
  shorts: Short[];
};

const textShadowValue = `1px 0 0 #065dc6,
                 -1px 0 0 #065dc6,
                 0 1px 0 #065dc6,
                 0 -1px 0 #065dc6,
                 1px 1px #065dc6,
                 -1px -1px 0 #065dc6,
                 1px -1px 0 #065dc6,
                 -1px 1px 0 #065dc6`;

const GAP_PX = 16;
const VISIBLE_AT_FULL_WIDTH = 5;
const MIN_ITEM_WIDTH_PX = 180;

const childSx = {
  borderRadius: '15px',
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#fff',
  p: 0,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#f0f0f0',
  },
} as const;

const arrowSx = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
  minWidth: 0,
  backgroundColor: '#fff',
  '&:hover': { backgroundColor: '#f0f0f0' },
} as const;

// A horizontally scrolling row that fits five shorts side by side when there is room and
// otherwise scrolls (touch, trackpad or the arrow buttons).
const YouTubeShorts = ({ shorts }: YouTubeShortsProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    setCanScrollBack(el.scrollLeft > 1);
    setCanScrollForward(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) {
      return undefined;
    }
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [shorts.length]);

  const scrollByPage = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (el) {
      el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
    }
  };

  const gapTotal = GAP_PX * (VISIBLE_AT_FULL_WIDTH - 1);

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {canScrollBack && (
        <Button aria-label='previous' onClick={() => scrollByPage(-1)} sx={{ ...arrowSx, left: (theme) => theme.spacing(1) }}>
          <ArrowBackIosIcon sx={{ ml: 0.5 }} />
        </Button>
      )}
      <Box
        onScroll={updateScrollState}
        ref={scrollRef}
        sx={{
          display: 'flex',
          gap: `${GAP_PX}px`,
          overflowX: 'auto',
          scrollSnapType: 'x proximity',
          p: 2.5,
          boxSizing: 'border-box',
          // Centers the row when everything fits, without clipping the start when it scrolls
          '& > :first-of-type': { ml: 'auto' },
          '& > :last-of-type': { mr: 'auto' },
        }}>
        {shorts?.map((short, i) => (
          <Box
            key={i}
            sx={{
              flex: '0 0 auto',
              width: `max(${MIN_ITEM_WIDTH_PX}px, calc((100% - ${gapTotal}px) / ${VISIBLE_AT_FULL_WIDTH}))`,
              maxWidth: '315px',
              aspectRatio: '9 / 16',
              borderRadius: '15px',
              overflow: 'hidden',
              scrollSnapAlign: 'start',
            }}>
            {short?.url ? (
              <Box
                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                allowFullScreen
                component='iframe'
                frameBorder='0'
                src={short.url}
                sx={childSx}
                title='YouTube video player'
              />
            ) : (
              <Button href={'https://www.youtube.com/@ecplay/shorts'} sx={{ ...childSx, position: 'relative' }}>
                <Box
                  alt='YouTube Short Thumbnail'
                  component='img'
                  src={ytShortsImage}
                  sx={{ filter: 'blur(2px)', borderRadius: '15px', position: 'absolute', height: '100%', width: '100%', objectFit: 'cover' }}
                />
                <Typography sx={{ fontSize: '30px', color: 'black', textShadow: textShadowValue, zIndex: 1 }} variant='h2'>
                  {short.text}
                </Typography>
              </Button>
            )}
          </Box>
        ))}
      </Box>
      {canScrollForward && (
        <Button aria-label='next' onClick={() => scrollByPage(1)} sx={{ ...arrowSx, right: (theme) => theme.spacing(1) }}>
          <ArrowForwardIosIcon />
        </Button>
      )}
    </Box>
  );
};

export default YouTubeShorts;
